import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import type { PDFPageProxy } from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import Loader from "ui-kit/src/Loader/Loader";
import { _ } from "i18n";
import "./css/PhysicalAnalysisPdfViewer.css";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

const PAGE_LOAD_BUFFER = 1;
/** ISO A4 height-to-width ratio; used until the first page is measured. */
const A4_HEIGHT_TO_WIDTH_RATIO = 297 / 210;

interface PhysicalAnalysisPdfViewerProps {
	url: string;
}

interface PdfPageSlotProps {
	pageNumber: number;
	isVisible: boolean;
	pageWidth: number;
	placeholderHeight: number;
	onVisible: (pageNumber: number) => void;
	onFirstPageMeasured?: (heightToWidthRatio: number) => void;
}

function PdfPageSlot({
	pageNumber,
	isVisible,
	pageWidth,
	placeholderHeight,
	onVisible,
	onFirstPageMeasured,
}: PdfPageSlotProps) {
	const sentinelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (sentinel === null) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (entry?.isIntersecting) {
					onVisible(pageNumber);
				}
			},
			{
				root: null,
				rootMargin: "400px 0px",
				threshold: 0,
			},
		);

		observer.observe(sentinel);

		return () => {
			observer.disconnect();
		};
	}, [onVisible, pageNumber]);

	return (
		<div ref={sentinelRef} className="physical-analysis-pdf-viewer__pageSlot">
			{isVisible ? (
				<Page
					pageNumber={pageNumber}
					width={pageWidth}
					renderTextLayer={false}
					renderAnnotationLayer={false}
					onRenderSuccess={
						pageNumber === 1 && onFirstPageMeasured !== undefined
							? (pdfPage: PDFPageProxy) => {
								const viewport = pdfPage.getViewport({ scale: 1 });
								onFirstPageMeasured(viewport.height / viewport.width);
							}
							: undefined
					}
					loading={
						<div className="physical-analysis-pdf-viewer__pageLoader">
							<Loader />
						</div>
					}
				/>
			) : (
				<div
					className="physical-analysis-pdf-viewer__pagePlaceholder"
						style={{ height: `${placeholderHeight}px` }}
					aria-hidden={true}
				/>
			)}
		</div>
	);
}

export function PhysicalAnalysisPdfViewer({ url }: PhysicalAnalysisPdfViewerProps) {
	const [numPages, setNumPages] = useState<number | null>(null);
	const [visiblePages, setVisiblePages] = useState<Set<number>>(() => new Set([1]));
	const [pageWidth, setPageWidth] = useState(0);
	const [pageHeightToWidthRatio, setPageHeightToWidthRatio] = useState(A4_HEIGHT_TO_WIDTH_RATIO);
	const [hasLoadError, setHasLoadError] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const placeholderHeight = Math.round(pageWidth * pageHeightToWidthRatio);

	useEffect(() => {
		setNumPages(null);
		setVisiblePages(new Set([1]));
		setPageHeightToWidthRatio(A4_HEIGHT_TO_WIDTH_RATIO);
		setHasLoadError(false);
	}, [url]);

	useLayoutEffect(() => {
		const container = containerRef.current;
		if (container === null) {
			return;
		}

		const updatePageWidth = (width: number) => {
			setPageWidth(Math.floor(width));
		};

		updatePageWidth(container.getBoundingClientRect().width);

		const resizeObserver = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (entry) {
				updatePageWidth(entry.contentRect.width);
			}
		});

		resizeObserver.observe(container);

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	const handleFirstPageMeasured = useCallback((heightToWidthRatio: number) => {
		setPageHeightToWidthRatio(heightToWidthRatio);
	}, []);

	const markPageVisible = useCallback((pageNumber: number) => {
		setVisiblePages((previousPages) => {
			const nextPages = new Set(previousPages);
			nextPages.add(pageNumber);

			for (let offset = 1; offset <= PAGE_LOAD_BUFFER; offset++) {
				const nextPage = pageNumber + offset;
				if (numPages !== null && nextPage <= numPages) {
					nextPages.add(nextPage);
				}

				const previousPage = pageNumber - offset;
				if (previousPage >= 1) {
					nextPages.add(previousPage);
				}
			}

			if (nextPages.size === previousPages.size) {
				return previousPages;
			}

			return nextPages;
		});
	}, [numPages]);

	const pdfDocumentOptions = useMemo(
		() => ({
			disableAutoFetch: true,
			disableStream: false,
		}),
		[],
	);

	if (hasLoadError) {
		return <p className="error-text">{_("gamePage.physicalMetrics.errorLoadingData")}</p>;
	}

	return (
		<div ref={containerRef} className="physical-analysis-pdf-viewer">
			<Document
				key={url}
				file={url}
				options={pdfDocumentOptions}
				onLoadSuccess={({ numPages: loadedPageCount }) => {
					setNumPages(loadedPageCount);
				}}
				onLoadError={() => {
					setHasLoadError(true);
				}}
				loading={
					<div className="physical-analysis-pdf-viewer__documentLoader">
						<Loader />
					</div>
				}
			>
				{numPages !== null &&
					pageWidth > 0 &&
					Array.from({ length: numPages }, (_, index) => {
						const pageNumber = index + 1;

						return (
							<PdfPageSlot
								key={pageNumber}
								pageNumber={pageNumber}
								isVisible={visiblePages.has(pageNumber)}
								pageWidth={pageWidth}
								placeholderHeight={placeholderHeight}
								onVisible={markPageVisible}
								onFirstPageMeasured={
									pageNumber === 1 ? handleFirstPageMeasured : undefined
								}
							/>
						);
					})}
			</Document>
		</div>
	);
}
